/**
 * Der Abgleich, der von selbst läuft.
 *
 * Er hängt hier und nicht im Sync-Reiter: Geschrieben wird im Formular, in der
 * Stammdatentabelle und beim Garmin-Import — wäre die Automatik an die
 * Sync-Ansicht gebunden, liefe sie genau dann nicht, wenn sie gebraucht wird.
 *
 * Dreissig Sekunden nach der letzten Änderung, und nur bei verbundenem
 * Anbieter — ohne Verbindung käme sonst bei jeder Änderung eine Fehlermeldung.
 *
 * **Der Check-in hängt hier mit drin, seit er eine eigene Datei hat.** Er
 * ändert sich selten, aber wenn, dann ist es das Unersetzlichste im ganzen
 * Bestand: Ein Tauchgang steht notfalls noch in `raw/`, eine Brevet-Nummer
 * nirgends. Auf einen Knopf, den jemand drücken muss, ist dabei kein Verlass —
 * wer den Browserspeicher leert, hat ihn nie gedrückt. Ausgelöst wird über ein
 * eigenes Signal (`beiCheckinAenderung`), weil die Ausweise nicht über
 * `saetze.js` geschrieben werden.
 */

import { gleicheDatenAb } from './datensync.js';
import { gleicheCheckinAb } from './checkinsync.js';
import { beiCheckinAenderung } from './checkin.js';
import { beiAenderung } from './saetze.js';
import { stelleBinaeresHer } from './speicher.js';
import { verzoegere } from './verzoegert.js';
import { SCHLUESSEL_ZULETZT, checkinAdapter, datenAdapter, istVerbunden, vergissRecht }
  from './views/sync.js';

export function starteAutomatik(db, {
  ms = 30000,
  uhr = globalThis,
  einstellungen = globalThis.localStorage,
  verbunden = istVerbunden,
  baueAdapter = datenAdapter,
  baueCheckinAdapter = checkinAdapter,
  melde = null,
} = {}) {
  // Der Abgleich schreibt selbst über `speichere()` und löste damit erneut aus.
  // Ohne diese Sperre drehte sich die Automatik ohne Zutun weiter.
  let laeuft = false;

  const automatik = verzoegere(async () => {
    if (laeuft || !verbunden()) return;
    laeuft = true;
    try {
      const ergebnis = await gleicheDatenAb(db, baueAdapter());
      einstellungen.setItem(SCHLUESSEL_ZULETZT, new Date().toISOString());
      melde?.(ergebnis);
    } catch (fehler) {
      // Kein Netz: Der nächste Anlauf holt es nach, die Änderung bleibt lokal.
      // Ein abgelaufenes Recht dagegen kommt von selbst nicht wieder — es muss
      // weg, damit die Oberfläche „nicht verbunden" sagt statt zu schweigen.
      if (fehler?.rechtWeg) vergissRecht(undefined, einstellungen);
    } finally {
      laeuft = false;
    }
  }, ms, uhr);

  const ausweise = verzoegere(async () => {
    if (!verbunden()) return;
    try {
      await gleicheCheckinAb(db, baueCheckinAdapter(), { stelleHer: stelleBinaeresHer });
      einstellungen.setItem(SCHLUESSEL_ZULETZT, new Date().toISOString());
    } catch (fehler) {
      if (fehler?.rechtWeg) vergissRecht(undefined, einstellungen);
    }
  }, ms, uhr);

  const abmelden = beiAenderung(() => {
    // Was der Abgleich selbst schreibt, ist keine neue Änderung.
    if (!laeuft) automatik.ausloesen();
  });
  const abmeldenCheckin = beiCheckinAenderung(() => ausweise.ausloesen());

  /**
   * Einmal beim Start — sonst holt nie jemand etwas.
   *
   * Ausgelöst hat der Abgleich bisher nur auf eine **eigene** Änderung. Auf
   * dem zweiten Gerät ändert sich aber nichts: Dort will man sehen, was am
   * ersten entstanden ist. Wer die App öffnete und angemeldet war, bekam
   * trotzdem nur den lokalen Stand zu sehen — beim Check-in war das der
   * gemeldete Fehler, bei den Tauchgängen derselbe, nur unbemerkt.
   */
  automatik.ausloesen();
  ausweise.ausloesen();

  return () => {
    automatik.abbrechen();
    ausweise.abbrechen();
    abmelden();
    abmeldenCheckin();
  };
}
